/**
 * PWAInstallPrompt
 *
 * Mounts near the top of the app tree.  Shows a small, dismissable "Install
 * on your home screen" banner for new visitors.
 *
 *   - Android / desktop Chrome / Edge: captures the `beforeinstallprompt`
 *     event and offers a one-tap Install button that triggers the browser
 *     install dialog.
 *   - iOS Safari / iPadOS: no programmatic install exists, so we show
 *     tap-the-Share-icon instructions with the correct glyph.
 *   - If the app is already running in standalone / installed mode
 *     (display-mode: standalone or navigator.standalone === true) the banner
 *     stays hidden.
 *   - The user can dismiss the banner; dismissal is remembered in
 *     localStorage for 30 days so we don't nag.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Share, Download, Plus } from 'lucide-react';

const DISMISS_KEY = 'arp:pwa-install-dismissed-at';
const DISMISSED_FOREVER_KEY = 'arp:pwa-install-dismissed-forever';
// Tudor review 9 Oct 2026: once a visitor closes the install banner it must
// never show again on that device (D3).  We keep the old timestamped key for
// back-compat but short-circuit on the boolean "forever" key first.
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days (legacy)

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
}

function isDismissedForever() {
  try {
    return localStorage.getItem(DISMISSED_FOREVER_KEY) === '1';
  } catch {
    return false;
  }
}

function isRecentlyDismissed() {
  if (isDismissedForever()) return true;
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - Number(v) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

// Only show the install banner once the visitor has engaged with the landing
// page (scrolled past half way) OR is already signed in (any protected route).
// Signed-in users will trigger the banner on the first dashboard load.
function isEligibleToShow() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '/';
  if (path !== '/' && path !== '/login' && !path.startsWith('/landing')) return true;
  const scrolled = (window.scrollY || window.pageYOffset || 0);
  const total = Math.max(1, (document.documentElement.scrollHeight || 0) - (window.innerHeight || 0));
  return (scrolled / total) >= 0.5;
}

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (isIOS || isIPadOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

export default function PWAInstallPrompt() {
  const platform = useMemo(() => detectPlatform(), []);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return undefined;

    const tryShow = () => {
      if (isEligibleToShow()) setVisible(true);
    };

    const onBeforeInstall = (e) => {
      // Prevent Chrome's auto-mini-infobar so we can present our own UI.
      e.preventDefault();
      setDeferredPrompt(e);
      tryShow();
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // Re-evaluate eligibility on scroll on the landing page.
    const onScroll = () => { if (isEligibleToShow()) tryShow(); };
    window.addEventListener('scroll', onScroll, { passive: true });

    // On iOS Safari there is no `beforeinstallprompt` — surface the banner
    // after the visitor is eligible (scrolled or signed-in).
    let iosTimer = null;
    if (platform === 'ios') {
      iosTimer = setTimeout(tryShow, 2500);
    } else {
      // Signed-in Chrome/Android: evaluate once after mount in case the
      // beforeinstallprompt fired before we attached the handler.
      setTimeout(tryShow, 1500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('scroll', onScroll);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [platform]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_FOREVER_KEY, '1');
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* localStorage disabled: banner will just reappear next visit */
    }
    setVisible(false);
    setShowIosSheet(false);
  }, []);

  const install = useCallback(async () => {
    if (platform === 'ios') {
      setShowIosSheet(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt, platform]);

  if (!visible) return null;

  // iOS instruction sheet — shown after the user taps "Install" on iOS.
  if (showIosSheet) {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 pointer-events-none"
        data-testid="pwa-install-ios-instructions"
      >
        <div className="pointer-events-auto max-w-md mx-auto bg-white border-2 border-zinc-900 rounded-2xl shadow-2xl p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-sm font-bold text-zinc-900">Add Axiom to your Home Screen</h3>
            <button
              onClick={dismiss}
              className="p-1 hover:bg-zinc-100 rounded"
              aria-label="Dismiss"
              data-testid="pwa-install-ios-dismiss"
            >
              <X className="w-4 h-4 text-zinc-500" />
            </button>
          </div>
          <ol className="space-y-2.5 text-xs text-zinc-700">
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center">1</span>
              <span className="flex items-center gap-1">
                Tap the <Share className="inline w-4 h-4 text-blue-500" aria-label="Share" /> <strong>Share</strong> icon in Safari's bottom bar.
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center">2</span>
              <span className="flex items-center gap-1">
                Scroll and tap <strong>Add to Home Screen</strong> <Plus className="inline w-4 h-4 text-zinc-600" aria-label="Add" />
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center">3</span>
              <span>Tap <strong>Add</strong> in the top-right: the Axiom icon appears on your Home Screen.</span>
            </li>
          </ol>
          <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
            Once installed, launch Axiom from your Home Screen for a full-screen, app-like experience: no browser bars, faster start.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-2 pb-2 pointer-events-none"
      data-testid="pwa-install-banner"
    >
      <div className="pointer-events-auto max-w-xs mx-auto bg-white border-2 border-black rounded-[10px] shadow p-2 flex items-center gap-2">
        <div className="flex-shrink-0 w-7 h-7 rounded-md bg-black flex items-center justify-center">
          <Download className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold text-black leading-tight">Install Axiom</p>
        </div>
        <button
          onClick={install}
          className="flex-shrink-0 px-2.5 py-1 bg-black text-white text-[11px] font-bold rounded-md hover:bg-zinc-800 active:scale-95 transition"
          data-testid="pwa-install-btn"
        >
          Install
        </button>
        <button
          onClick={dismiss}
          className="flex-shrink-0 p-1 hover:bg-zinc-100 rounded"
          aria-label="Dismiss install banner"
          data-testid="pwa-install-dismiss-btn"
        >
          <X className="w-3.5 h-3.5 text-black" />
        </button>
      </div>
    </div>
  );
}
